/**
 * useModuleAccess - Hook para verificar acceso a módulos
 * 
 * Verifica si el usuario actual tiene acceso a un módulo específico
 * basado en la configuración del admin.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache de configuración de módulos
let moduleConfigCache = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60000; // 1 minuto

export const useModuleAccess = (moduleId) => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [message, setMessage] = useState(null);
  const [isAdminOverride, setIsAdminOverride] = useState(false);

  const checkAccess = useCallback(async () => {
    if (!user || !moduleId) {
      setLoading(false);
      return;
    }

    // Admin siempre tiene acceso
    if (user.role === "admin") {
      setHasAccess(true);
      setIsAdminOverride(false);
      setLoading(false);
      return;
    }

    try {
      // Verificar cache
      const now = Date.now();
      if (moduleConfigCache && (now - cacheTimestamp) < CACHE_TTL) {
        const moduleConfig = moduleConfigCache[moduleId];
        if (moduleConfig) {
          setHasAccess(moduleConfig.enabled);
          setMessage(moduleConfig.enabled ? null : moduleConfig.coming_soon_message);
          setIsAdminOverride(moduleConfig.is_admin_override || false);
          setLoading(false);
          return;
        }
      }

      // Obtener configuración desde API
      const response = await axios.get(
        `${API_URL}/api/app-config/modules`,
        { headers: getAuthHeaders() }
      );

      // Actualizar cache
      moduleConfigCache = response.data.modules;
      cacheTimestamp = now;

      const moduleConfig = moduleConfigCache[moduleId];
      if (moduleConfig) {
        setHasAccess(moduleConfig.enabled);
        setMessage(moduleConfig.enabled ? null : moduleConfig.coming_soon_message);
        setIsAdminOverride(moduleConfig.is_admin_override || false);
      }
    } catch (error) {
      console.error("Error checking module access:", error);
      // En caso de error, permitir acceso por defecto para no bloquear la app
      setHasAccess(true);
    } finally {
      setLoading(false);
    }
  }, [user, moduleId, getAuthHeaders]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  return { loading, hasAccess, message, isAdminOverride, refetch: checkAccess };
};

/**
 * Función para invalidar el cache de módulos
 * Útil cuando el admin hace cambios
 */
export const invalidateModuleCache = () => {
  moduleConfigCache = null;
  cacheTimestamp = 0;
};

export default useModuleAccess;
